import axios from 'axios';
import UserPool from './UserPool';
import { CognitoUser } from 'amazon-cognito-identity-js';

// Determine the base URL based on the environment
const baseURL =
  process.env.NODE_ENV === 'production'
    ? 'https://backend.real-time.ca/' // Replace with your API Gateway URL
    : 'http://127.0.0.1:8000'; // Local development URL

// Create an axios instance with the base URL configured
const api = axios.create({
  baseURL: baseURL,
});

// Function to get fresh tokens from Cognito
const refreshTokens = async () => {
  const user = UserPool.getCurrentUser();
  if (!user) {
    throw new Error('No current user');
  }

  return new Promise((resolve, reject) => {
    user.getSession((err, session) => {
      if (err) {
        reject(err);
        return;
      }

      const idToken = session.getIdToken().getJwtToken();
      const accessToken = session.getAccessToken().getJwtToken();

      // Update localStorage with fresh tokens
      localStorage.setItem('idToken', idToken);
      localStorage.setItem('accessToken', accessToken);

      resolve({
        idToken,
        accessToken
      });
    });
  });
};

// Add a request interceptor to attach the token (if present)
api.interceptors.request.use(
  async (config) => {
    try {
      // Always try to get fresh tokens before making a request
      const { idToken } = await refreshTokens();
      config.headers.Authorization = `Bearer ${idToken}`;
      consoleDevLog('Setting fresh token in request');
    } catch (error) {
      consoleDevLog('Error refreshing tokens:', error);
      // If we can't refresh tokens, try to use existing token
      const token = window.localStorage.getItem('idToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    consoleDevLog('Request config', config);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Function to check session validity
const checkSessionValidity = () => {
  return new Promise((resolve) => {
    const user = UserPool.getCurrentUser();
    if (user) {
      user.getSession((err, session) => {
        if (err || !session || !session.isValid()) {
          consoleDevLog('Session invalid:', err);
          resolve(false);
        } else {
          resolve(true);
        }
      });
    } else {
      resolve(false);
    }
  });
};

// Add a response interceptor to handle 401 Unauthorized errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    if (error.response && error.response.status === 401) {
      consoleDevLog('Received 401 Unauthorized response:', error);
      
      try {
        // Try to refresh tokens first
        await refreshTokens();
        
        // If refresh successful, retry the original request
        const { config } = error;
        return api(config);
      } catch (refreshError) {
        consoleDevLog('Failed to refresh tokens:', refreshError);
        
        // Check if session is still valid
        const isSessionValid = await checkSessionValidity();
        
        if (!isSessionValid) {
          consoleDevLog('Session is invalid, logging out');
          // Clear tokens from localStorage
          window.localStorage.removeItem('idToken');
          window.localStorage.removeItem('accessToken');
          window.localStorage.removeItem('refreshToken');
          
          // Redirect to login page
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

export function consoleDevLog(...args) {
  if (process.env.NODE_ENV !== 'production') {
    console.log(...args);
  }
}

// Export the instance for use in components
export default api;
